import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import styles from './marketing.module.css';
import { SEO } from '../components/SEO.jsx';
import { What_is_BSB as WhatIsBSB } from './what_is_BSB.jsx';
import { UseCases } from './use_cases.jsx';
import { Money } from './money.jsx';
import { HowItWorks } from './how_it_works.jsx';
import { PromptSecurity } from './prompt_security.jsx';

import heroImage from '../pictures/Blockchain box with people.avif';
import useCaseImage from '../pictures/Modern Shop with People.png';
import currencyImage from '../pictures/Currency.webp';
import aiImage from '../pictures/AI agent bought handbag.webp';
import apiImage from '../pictures/API.webp';
import libraryImage from '../pictures/Library.avif';

const overviewCards = [
  {
    tag: 'Trust platform',
    title: 'A stronger proof layer for digital and physical assets',
    text: 'Use Back-Step Blockchain where customers, partners, or regulators need confidence that a record, product, or claim has not been silently changed.',
    image: heroImage,
    detail: 1,
  },
  {
    tag: 'Use cases',
    title: 'Built for markets, not only for technologists',
    text: 'Luxury goods, certificates, business records, supply chains, and digital money all benefit from proof that is easy to explain.',
    image: useCaseImage,
    detail: 2,
  },
  {
    tag: 'Business value',
    title: 'Turn security into a customer promise',
    text: 'The technology can sit behind products and services while the customer sees a simple message: this can be verified.',
    image: libraryImage,
    detail: 3,
  },
  {
    tag: 'Digital money',
    title: 'A foundation for stable digital value',
    text: 'Back-Step Blockchain supports a new conversation around trusted digital currencies and practical everyday payment models.',
    image: currencyImage,
    detail: 4,
  },
  {
    tag: 'AI trust',
    title: 'Proof and accountability for AI-driven activity',
    text: 'As AI agents create, buy, decide, and negotiate, trusted records become part of the product experience.',
    image: aiImage,
    detail: 5,
  },
  {
    tag: 'Builders',
    title: 'APIs for new applications',
    text: 'Partners can build customer-facing applications on top of the platform instead of starting from raw blockchain infrastructure.',
    image: apiImage,
    link: '/contact',
  },
];

export const Overview = () => {
  const [selectedForm, setSelectedForm] = useState(0);
  const location = useLocation();
  const prevLocationState = useRef(location.state);

  useEffect(() => {
    if (prevLocationState.current !== location.state) {
      setSelectedForm(0);
      prevLocationState.current = location.state;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.state]);

  switch (selectedForm) {
    case 1:
      return <WhatIsBSB />;
    case 2:
      return <UseCases />;
    case 3:
      return <HowItWorks />;
    case 4:
      return <Money />;
    case 5:
      return <PromptSecurity />;
    default:
      break;
  }

  return (
    <main className={styles.page}>
      <SEO
        title="Back-Step Blockchain Overview"
        description="A customer-oriented overview of Back-Step Blockchain, its markets, product authenticity use cases, AI accountability, digital money, and practical value."
        path="/overview"
      />

      <section className={styles.hero}>
        <img className={styles.heroImage} src={heroImage} alt="People reviewing blockchain trust infrastructure" />
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <p className={styles.kicker}>Overview</p>
          <h1 className={styles.heroTitle}>Blockchain trust, explained in business terms.</h1>
          <p className={styles.heroText}>
            Back-Step Blockchain is a platform for companies that need proof, authenticity, and reliable records
            without asking their customers to learn the engineering behind it.
          </p>
          <div className={styles.actions}>
            <Link className={styles.primaryAction} to="/products">See products</Link>
            <Link className={styles.secondaryAction} to="/story">Patent story</Link>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <p className={styles.kicker}>Where it fits</p>
            <h2>Start with the trust problem your customer already understands.</h2>
            <p>
              The detailed science is important, but the first question is commercial: what valuable thing needs proof,
              and who benefits when that proof is simple to verify?
            </p>
          </div>

          <div className={styles.grid}>
            {overviewCards.map((card) => (
              <article className={styles.card} key={card.title}>
                <img className={styles.cardImage} src={card.image} alt="" />
                <div className={styles.cardBody}>
                  <span className={styles.tag}>{card.tag}</span>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                  {card.detail ? (
                    <button className={styles.cardButton} type="button" onClick={() => setSelectedForm(card.detail)}>
                      Read the details
                    </button>
                  ) : (
                    <Link className={styles.cardButton} to={card.link}>Discuss an application</Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};
